import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';

import { createLocalStorage, isEmpty, isUndefined } from '~libs.typescript';
import { MedicineSinglePackageUnit } from '~libs.shared.medicine';

import { PreciseDosageUnit, PreciseDosageUnits, PreferredDosageUnit } from '../types';

import { hasCompositePackage } from './medicineSufficiency';

const PRECISE_DOSAGE_UNITS: PreciseDosageUnits = [
   'amp.',
   'amp.-strzyk.',
   'aplik.',
   'czopek',
   'czopków',
   'daw.',
   'dm3',
   'fiol.',
   'g',
   'j.m.',
   'jednostek',
   'kaps.',
   'kg',
   'kropla',
   'krople',
   'kropli',
   'l',
   'm',
   'm3',
   'masa',
   'mcg',
   'mcl',
   'mg',
   'ml',
   'objętość',
   'past.',
   'plaster',
   'plastrów',
   'plastry',
   'poj.',
   'pojemnik',
   'sasz.',
   'szt.',
   'tabl.',
   'worek',
   'worków',
] as const;

export const getPreciseDosageUnits = (): PreciseDosageUnits => {
   return PRECISE_DOSAGE_UNITS;
};

export const asPreciseDosageUnit = (unit: string | undefined): PreciseDosageUnit | undefined => {
   return isUndefined(unit) ? undefined : (unit as PreciseDosageUnit);
};

type PreferredDosageUnitKey = `medicineId-${number}` | `medicineEanCode-${string}`;

type PreferredDosageUnitMap = Record<PreferredDosageUnitKey, PreferredDosageUnit>;

const createPreferredDosageUnitKey = (medicine: MedicineSimple): PreferredDosageUnitKey => {
   return isEmpty(medicine.eanCode) ? `medicineId-${medicine.id}` : `medicineEanCode-${medicine.eanCode}`;
};

const preferredDosageUnitLocalStorage = createLocalStorage<PreferredDosageUnitMap>(
   'prescription:preferredDosageUnit',
   'scoped'
);

export const getPreferredDosageUnit = (medicine: MedicineSimple): PreferredDosageUnit | undefined => {
   const preferredDosageUnitKey = createPreferredDosageUnitKey(medicine);
   return preferredDosageUnitLocalStorage.getValue()?.[preferredDosageUnitKey];
};

export const setPreferredDosageUnit = (
   medicine: MedicineSimple,
   preferredDosageUnit: PreferredDosageUnit | undefined
): void => {
   const preferredDosageUnitKey = createPreferredDosageUnitKey(medicine);
   preferredDosageUnitLocalStorage.setValue((previousPreferredDosageUnitsMap) => ({
      ...previousPreferredDosageUnitsMap,
      [preferredDosageUnitKey]: preferredDosageUnit,
   }));
};

export const getSortedPreciseDosageUnits = (
   medicine: MedicineSimple
): Array<PreciseDosageUnit | MedicineSinglePackageUnit> => {
   const sortedPreciseDosageUnits = Array.from(getPreciseDosageUnits()).sort((prevUnit, nextUnit) => {
      if (prevUnit === medicine.singlePackageUnit) {
         return -1;
      }
      if (nextUnit === medicine.singlePackageUnit) {
         return 1;
      }

      const preferredDosageUnit = getPreferredDosageUnit(medicine);
      if (prevUnit === preferredDosageUnit) {
         return -1;
      }
      if (nextUnit === preferredDosageUnit) {
         return 1;
      }
      return 0;
   });

   return medicine.singlePackageUnit && sortedPreciseDosageUnits.every((unit) => unit !== medicine.singlePackageUnit)
      ? [medicine.singlePackageUnit, ...sortedPreciseDosageUnits]
      : sortedPreciseDosageUnits;
};

export const isDosageUnitEditable = (medicine: MedicineSimple | undefined): boolean => {
   return hasCompositePackage(medicine) || isEmpty(medicine?.singlePackageUnit);
};

export const getDefaultDosageUnit = (medicine: MedicineSimple): PreferredDosageUnit | undefined => {
   return isDosageUnitEditable(medicine) ? getPreferredDosageUnit(medicine) : medicine.singlePackageUnit;
};
